import { withFormik } from 'formik';
import compact from 'lodash/compact';
import { connect } from 'react-redux';
import { compose, setDisplayName } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { extractValidationErrors } from 'src/app/popsicle/utils';
import { segmentTrack } from 'src/app/state/actions';
import { accountLoadEverything, updatePrimaryPatientInfo } from 'src/app/state/actions/account';
import { pushNextCheckoutState } from 'src/app/state/actions/checkout';
import { getPrimaryPatient } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';

import { MEDICATIONS_DELIMITER, splitMedications } from '../utils';

import validation from './validation';

import Renderer from '.';

const checkIfNone = (attribute: string): string | null => {
  // We should remove our usage of sending a none string to the backend so we do not need this in the future
  // Users could technically write None or none, we should have some type of way of differentiating that
  return attribute?.toLowerCase() === 'none' ? '' : attribute;
};

const mapStateToProps = () => {
  return (state: RootState) => {
    const primaryPatient = getPrimaryPatient(state);
    const allergies = checkIfNone(primaryPatient?.allergies ?? null);
    const conditions = checkIfNone(primaryPatient?.medicalConditions ?? null);
    const medicationStrings: string | null = primaryPatient?.otherMedications ?? null;
    const medications = medicationStrings ? splitMedications(medicationStrings) : null;

    return {
      allergies,
      conditions,
      medications,
    };
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  dispatchLoadAccount: async () => await dispatch(accountLoadEverything()),
  dispatchSaveAllergies: async values => {
    await dispatch(updatePrimaryPatientInfo(values));
    await dispatch(pushNextCheckoutState());
  },
  dispatchSegmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
});

const enhancedWithFormik = withFormik({
  displayName: 'AllergyProfileForm',
  isInitialValid: props => {
    const { allergies, conditions, medications } = props;
    return allergies !== null && conditions !== null && medications !== null;
  },
  enableReinitialize: true,
  mapPropsToValues: props => {
    const { allergies, conditions, medications, doctors } = props;

    interface FormValues {
      allergies: string;
      allergiesToggle: 'Yes' | 'No' | '';
      conditions: string;
      conditionsToggle: 'Yes' | 'No' | '';
      medications: string[];
      medicationsToggle: 'Yes' | 'No' | '';
      doctors: string[];
    }

    // We have to keep '' in here because on the initial load, product wants the button to be depressed with nothing
    // click. If you pass in a blank string and not 'Yes' or 'No', then none of the toggle will be pressed.
    const formValues: FormValues = {
      allergies,
      allergiesToggle: allergies ? 'Yes' : allergies !== null ? 'No' : '',
      conditions,
      conditionsToggle: conditions ? 'Yes' : conditions !== null ? 'No' : '',
      medications,
      medicationsToggle: medications === null ? '' : medications[0] ? 'Yes' : 'No',
      doctors: doctors ? doctors : [],
    };
    return formValues;
  },
  validationSchema: validation.schema,
  validateOnChange: true,
  validateOnBlur: true,
  handleSubmit: async (values, { props, setErrors, setSubmitting }) => {
    const { dispatchSaveAllergies, dispatchSegmentTrack } = props;
    const { allergiesToggle, allergies, conditionsToggle, conditions, medicationsToggle, medications, doctors } =
      values;
    setSubmitting(true);

    const joinedMedications = medications.join(MEDICATIONS_DELIMITER);
    const joinedDoctors = compact(doctors).join(MEDICATIONS_DELIMITER);

    try {
      const formValues = {
        allergiesToggle: allergiesToggle || '',
        allergies: allergies || '',
        conditionsToggle: conditionsToggle || '',
        conditions: conditions || '',
        medicationsToggle: medicationsToggle || '',
        medications: joinedMedications || '',
        doctors: joinedDoctors || '',
      };

      const requestValues = {
        allergies: values.allergies || 'None',
        medicalConditions: values.conditions || 'None',
        otherMedications: joinedMedications || 'None',
        hasOtherMeds: !!joinedMedications,
        potentialTransfers: null,
      };

      await dispatchSaveAllergies(requestValues);
      dispatchSegmentTrack(UNIFIED.saveCheckoutAllergyProfile, { ...formValues });
    } catch (error) {
      setErrors(extractValidationErrors(error));
      setSubmitting(false);
    }
  },
});

export const AllergyProfile = compose(
  setDisplayName('AllergyProfile'),
  connect(mapStateToProps, mapDispatchToProps),
  enhancedWithFormik
)(Renderer);
